import { cn } from '@/lib/utils';
import { useEffect  } from 'react';
import type {ReactNode} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Footer } from '@/components/layout/footer';
import { Header } from '@/components/layout/header';
import { Container } from '@/components/ui/primitives';

/**
 * Gabarit commun à toutes les pages : en-tête, contenu, pied de page.
 *
 * Le titre du document est mis à jour ici. Sur une application à page unique,
 * rien ne le fait automatiquement : sans cette ligne, l'onglet du navigateur
 * et l'historique conserveraient le titre de la page d'accueil sur tout le
 * site.
 */
export function Page({
    title,
    children,
    className,
}: {
    /** Titre de l'onglet, sans le nom du site (ajouté automatiquement). */
    title: string;
    children: ReactNode;
    className?: string;
}) {
    useEffect(() => {
        document.title = `${title} — Helvedoc`;
    }, [title]);

    return (
        <div className="flex min-h-screen flex-col">
            <Header />
            <main className={cn('flex-1', className)}>{children}</main>
            <Footer />
        </div>
    );
}

/**
 * Bandeau de titre des pages internes (compte, contact, résiliation…).
 * Les pages éditoriales — accueil, tarif — ont leur propre mise en page.
 */
export function PageHeader({
    eyebrow,
    title,
    description,
    children,
}: {
    eyebrow?: string;
    title: string;
    description?: ReactNode;
    children?: ReactNode;
}) {
    return (
        <div className="border-line bg-surface-sunken border-b">
            <Container className="py-10 sm:py-14">
                {eyebrow && (
                    <p className="text-brand-600 mb-2 text-xs font-semibold tracking-[0.12em] uppercase">{eyebrow}</p>
                )}
                <h1 className="font-display text-3xl tracking-tight sm:text-4xl">{title}</h1>
                {description && <p className="text-ink-soft mt-3 max-w-2xl leading-relaxed">{description}</p>}
                {children && <div className="mt-6">{children}</div>}
            </Container>
        </div>
    );
}

/**
 * Fil d'Ariane simple, utilisé sur la fiche entreprise.
 * Le dernier élément porte `aria-current="page"`, comme le veut la norme.
 */
export function Breadcrumb({ items }: { items: Array<{ label: string; to?: string }> }) {
    return (
        <nav aria-label="Fil d'Ariane">
            <ol className="text-ink-muted flex flex-wrap items-center gap-1.5 text-xs">
                {items.map((item, index) => (
                    <li key={`${item.label}-${index}`} className="flex items-center gap-1.5">
                        {index > 0 && <span aria-hidden>/</span>}
                        {item.to ? (
                            <Link to={item.to} className="hover:text-brand-700 transition-colors">
                                {item.label}
                            </Link>
                        ) : (
                            <span aria-current="page" className="text-ink-soft">
                                {item.label}
                            </span>
                        )}
                    </li>
                ))}
            </ol>
        </nav>
    );
}

/**
 * Remet la page en haut à chaque navigation.
 *
 * `react-router` conserve volontairement la position de défilement : sur un
 * site classique, arriver au milieu de la page suivante est déroutant. Ce
 * composant, monté une fois dans `App`, corrige ce comportement.
 */
export function ScrollToTop() {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [pathname]);

    return null;
}
